// HomeworkTracker.js - Main Application Component
// 
// 【構造概要】960行 - 以下の責任を担当:
// 1. 認証管理 (currentUser, handleLogin)
// 2. データ管理 (students, teachers, tasks + Firebase接続)
// 3. UI状態管理 (15個のstate変数)
// 4. タスク操作 (CRUD, D&D, Excel export)
// 5. ページルーティング (renderTabContent)
// 6. レンダリング (メインUI構造)
//
// 【今後の分割計画】
// - Phase1: 最小リスクでの構造整理
// - Phase2: 単一関数の慎重な分離
// - Phase3: カスタムフック抽出

// SubjectTaskBoard.js

import React, { useState, useMemo, useCallback, Suspense, lazy } from 'react';
import { database } from './config/firebase';
import { ConfirmDialog } from './components/modals';
import { exportStudentTasksToExcel } from './utils/excelExport';
import { createMessagingHandlers, createNotesModalHandlers } from './utils/handlerUtils';
import { executeBulkImport } from './utils/bulkImportUtils';
import { handleDragEnd as handleDragEndUtil } from './utils/dragDropUtils';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import {
  PlusCircle
} from 'lucide-react';

import {
  ref,
  push,
  set,
  remove,
  update
} from 'firebase/database';
import { v4 as uuidv4 } from 'uuid';
import { hashPassword, isPasswordHashed } from './utils/authUtils';
import { Login } from './components/forms';
import { Navbar, Sidebar } from './components/layout';
import { MessagingModal } from './components/modals';
import { IndividualAssignments, SchoolTaskSummary, QuizManagement } from './components/pages';
import { QuizSection } from './components/quiz/QuizSection';

// 定数とユーティリティのimport
import { FILTERS, SUBJECTS } from './constants';
import { generatePageList } from './constants/pages';
import { filterTasks } from './utils/taskFilterUtils';
import { isTeacherUser } from './utils/authHelpers';

// 分離したコンポーネントのimport
import { SubjectTabs, TaskFilter } from './components/UI';
import { TaskCard, AddTaskForm } from './components/task';

// カスタムフックのimport
import { useAuth } from './hooks/useAuth';
import { useTasksData } from './hooks/useTasksData';
import { useTeacherManagement } from './hooks/useTeacherManagement';
import { useStudentSelection } from './hooks/useStudentSelection';

// 統合ログシステム
import { createLogger } from './utils/logger';

// 🎯 Code Splitting: 動的インポート
const BulkTaskImport = lazy(() => import('./components/forms/BulkTaskImport'));
const TeacherNotesModal = lazy(() => import('./components/modals/TeacherNotesModal'));

// メインコンポーネント
const SubjectTaskBoard = () => {
  // 統合ログシステム初期化
  const logger = createLogger('HomeworkTracker');
  
  // カスタムフックの使用
  const {
    currentUser,
    students,
    teachers,
    isDataLoaded,
    handleLogin,
    handleLogout
  } = useAuth();

  const { tasks } = useTasksData();

  const {
    handleAddTeacher,
    handleUpdateTeacher,
    handleDeleteTeacher
  } = useTeacherManagement();

  // 生徒選択の状態管理
  const {
    selectedStudent,
    setSelectedStudent,
    getStudentId,
    resetStudentSelection
  } = useStudentSelection(currentUser);

  // UI状態管理
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(SUBJECTS[0]);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('students');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isMessaging, setIsMessaging] = useState(false);
  const [messagingPartner, setMessagingPartner] = useState(null);
  const [selectedPage, setSelectedPage] = useState('schoolWork');
  const [selectedFilter, setSelectedFilter] = useState(FILTERS[0]);

  // isTeacher の定義
  const isTeacher = isTeacherUser(currentUser);

  // PAGESを動的に生成
  const pages = useMemo(() => {
    return generatePageList(isTeacher, currentUser?.isMaster);
  }, [isTeacher, currentUser]);

  // エクセルファイルの書き出し
  const exportToExcel = useCallback(async () => {
    await exportStudentTasksToExcel(selectedStudent, tasks);
  }, [selectedStudent, tasks]);

  // ハンドラー関数
  const { handleOpenMessaging, handleCloseMessaging } = createMessagingHandlers(
    setMessagingPartner,
    setIsMessaging
  );

  const { handleOpenNotesModal, handleCloseNotesModal } = createNotesModalHandlers(
    setIsNotesModalOpen
  );

  const handleBulkImport = useCallback(async (processedData) => {
    await executeBulkImport(processedData, tasks, setShowBulkImport);
  }, [tasks]);

  // 課題の並び替えを処理する関数
  const handleDragEnd = useCallback(async (result) => {
    await handleDragEndUtil(result, {
      students,
      tasks,
      getStudentId,
      selectedSubject,
      currentUser
    });
  }, [students, tasks, getStudentId, selectedSubject, currentUser]);

  // getStudentIdはuseStudentSelectionカスタムフックから取得

  // 合否判定の処理
  const handlePassFailItem = useCallback(async (taskId, itemId, status) => {
    try {
      const studentId = getStudentId();
      const key = `${studentId}-${selectedSubject.id}`;
      const itemRef = ref(database, `tasks/${key}/${taskId}/items/${itemId}`);
      
      // 同じステータスをクリックした場合は解除する
      const currentItem = tasks[key]?.[taskId]?.items?.[itemId];
      const newStatus = currentItem?.passFailStatus === status ? null : status;
      
      await update(itemRef, { passFailStatus: newStatus });
    } catch (error) {
      logger.error('合否判定の更新に失敗しました', { 
        error: error.message,
        taskId,
        itemId,
        status
      });
      alert('合否判定の更新に失敗しました。再度お試しください。');
    }
  }, [getStudentId, selectedSubject, tasks, logger]);

  // ハンドラー関数は一度だけ宣言
  const handleDeleteItem = useCallback(async (taskId, itemId) => {
    const studentId = getStudentId();
    const key = `${studentId}-${selectedSubject.id}`;
    const itemRef = ref(database, `tasks/${key}/${taskId}/items/${itemId}`);
    try {
      await remove(itemRef);
    } catch (error) {
      logger.error('チェック項目の削除に失敗しました', { 
        error: error.message,
        taskId,
        itemId
      });
      alert('チェック項目の削除に失敗しました。再度お試しください。');
    }
  }, [getStudentId, selectedSubject, logger]);

  const handleUpdateItem = useCallback(async (taskId, itemId, updatedItem) => {
    try {
      const studentId = getStudentId();
      const key = `${studentId}-${selectedSubject.id}`;
      const itemRef = ref(database, `tasks/${key}/${taskId}/items/${itemId}`);
      await update(itemRef, updatedItem);
    } catch (error) {
      logger.error('チェック項目の更新に失敗しました', { 
        error: error.message,
        taskId,
        itemId,
        updatedItem
      });
      alert('チェック項目の更新に失敗しました。再度お試しください。');
    }
  }, [getStudentId, selectedSubject, logger]);

  // タスクリストをレンダリングする関数を定義
  const renderTaskList = () => {
    const studentId = getStudentId();
    if (!studentId) return null;
    const key = `${studentId}-${selectedSubject.id}`;

    const taskList = tasks[key]
      ? Object.values(tasks[key]).sort((a, b) => (a.order || 0) - (b.order || 0))
      : [];

    // フィルタリング機能を追加
    const filteredTaskList = filterTasks(taskList, selectedFilter);

    return (
      <Droppable droppableId="tasksDroppable" type="task">
        {(provided) => (
          <div
            className="space-y-6"
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {filteredTaskList.map((task, index) => (
              <Draggable
                key={task.id}
                draggableId={`task-${task.id}`}
                index={index}
                isDragDisabled={!isTeacher}
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <TaskCard
                      task={task}
                      isTeacher={isTeacher}
                      onDeleteTask={() => {
                        setConfirmDialog({
                          title: '課題の削除',
                          message: 'この課題を削除してもよろしいですか？',
                          onConfirm: () => {
                            remove(ref(database, `tasks/${key}/${task.id}`));
                            setConfirmDialog(null);
                          },
                          onCancel: () => setConfirmDialog(null),
                        });
                      }}
                      onUpdateTask={(updatedTask) => {
                        set(ref(database, `tasks/${key}/${updatedTask.id}`), updatedTask);
                      }}
                      onCheckItem={(taskId, itemId) => {
                        const itemRef = ref(
                          database,
                          `tasks/${key}/${taskId}/items/${itemId}`
                        );
                        const currentItem = tasks[key]?.[taskId]?.items?.[itemId];
                        if (currentItem) {
                          const currentChecked = currentItem.checked || false;
                          update(itemRef, { checked: !currentChecked })
                            .then(() => {
                              // チェック状態の更新完了
                            })
                            .catch((error) => {
                              logger.error('チェック状態の更新に失敗しました', { 
                                error: error.message,
                                taskId,
                                itemId
                              });
                              alert('チェック状態の更新に失敗しました。再度お試しください。');
                            });
                        }
                      }}
                      onTeacherCheckItem={(taskId, itemId, status) => {
                        const itemRef = ref(
                          database,
                          `tasks/${key}/${taskId}/items/${itemId}`
                        );
                        update(itemRef, { teacherCheck: status })
                          .then(() => {
                            // 教師確認の更新完了
                          })
                          .catch((error) => {
                            logger.error('教師確認の更新に失敗しました', { 
                              error: error.message,
                              taskId,
                              itemId,
                              status
                            });
                            alert('教師確認の更新に失敗しました。再度お試しください。');
                          });
                      }}
                      onPassFailItem={handlePassFailItem}
                      onUpdateItem={handleUpdateItem}
                      onDeleteItem={handleDeleteItem}
                      getStudentId={getStudentId}
                      selectedSubject={selectedSubject}
                      currentUser={currentUser}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    );
  };

  // ローディング状態とログイン状態の管理
  if (!isDataLoaded) {
    return <div className="text-purple-700">データを読み込み中...</div>;
  }

  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  // タブのコンテンツをレンダリングする関数を定義
  const renderTabContent = () => {
    switch (selectedPage) {
      case 'QuizManagement':
        return <QuizManagement currentUser={currentUser} />;
      case 'schoolWork':
        if (!currentUser?.isMaster) {
          return <SchoolTaskSummary /* 適切なプロパティを渡す */ />;
        }
        return (
          <>
            <SubjectTabs
              subjects={SUBJECTS}
              selectedSubject={selectedSubject}
              onSelectSubject={setSelectedSubject}
            />

            {isTeacher && currentUser?.isMaster && selectedStudent && (
              <div className="mb-6">
                <button
                  onClick={() => setShowBulkImport(!showBulkImport)}
                  className="w-full py-4 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-500 hover:text-purple-500 flex items-center justify-center gap-2 transition-colors duration-200"
                  aria-label="課題を一括登録"
                >
                  一括登録
                </button>

                {/* BulkTaskImport コンポーネントを条件付きでレンダリング */}
                {showBulkImport && (
                  <Suspense fallback={<div className="text-center py-4 text-purple-600">一括登録機能を読み込み中...</div>}>
                    <BulkTaskImport
                      students={students}
                      onImport={handleBulkImport}
                      selectedStudent={selectedStudent}
                      selectedSubject={selectedSubject}
                      tasksByStudentAndSubject={
                        tasks[`${selectedStudent.id}-${selectedSubject.id}`]
                      }
                    />
                  </Suspense>
                )}
              </div>
            )}

            {/* フィルターコンポーネントを追加 */}
            {selectedStudent && (
              <TaskFilter
                selectedFilter={selectedFilter}
                onFilterChange={setSelectedFilter}
                className="mb-6"
              />
            )}

            <div className="space-y-6">
              {renderTaskList()}

              {isTeacher && currentUser?.isMaster && selectedStudent && (
                isAddingTask ? (
                  <AddTaskForm
                    onAddTask={(newTask) => {
                      const key = `${selectedStudent.id}-${selectedSubject.id}`;
                      const taskId = uuidv4();
                      const taskWithId = {
                        ...newTask,
                        id: taskId,
                      };
                      set(ref(database, `tasks/${key}/${taskWithId.id}`), taskWithId);
                      setIsAddingTask(false);
                    }}
                    onCancel={() => setIsAddingTask(false)}
                  />
                ) : (
                  <button
                    onClick={() => setIsAddingTask(true)}
                    className="w-full py-4 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-500 hover:text-purple-500 flex items-center justify-center gap-2 transition-colors duration-200"
                    aria-label="新しい課題を追加"
                  >
                    <PlusCircle size={20} />
                    新しい課題を追加
                  </button>
                )
              )}
            </div>
          </>
        );
      case 'individualAssignments':
        return (
          // 【個別課題】ページのコンテンツ
          <IndividualAssignments
            selectedStudent={selectedStudent}
            currentUser={currentUser}
          />
        );
      case 'schoolTaskSummary':
        return (
          <SchoolTaskSummary
            tasks={tasks}
            selectedStudent={selectedStudent || currentUser}
            subjects={SUBJECTS}
            isTeacher={isTeacher}
            onCheckItem={(subjectId, taskId, itemId) => {
              // チェックボックスの更新（教師・生徒共に使用可能）
              const studentId = getStudentId();
              if (!studentId) return;

              const key = `${studentId}-${subjectId}`;
              const itemRef = ref(database, `tasks/${key}/${taskId}/items/${itemId}`);

              const currentItem = tasks[key]?.[taskId]?.items?.[itemId];

              if (currentItem) {
                const currentChecked = currentItem.checked || false;
                update(itemRef, { checked: !currentChecked })
                  .then(() => {
                    // チェック状態の更新完了
                  })
                  .catch((error) => {
                    logger.error('チェック状態の更新に失敗しました', { 
                      error: error.message,
                      subjectId,
                      taskId,
                      itemId
                    });
                    alert('チェック状態の更新に失敗しました。再度お試しください。');
                  });
              }
            }}
            onTeacherCheckItem={(subjectId, taskId, itemId, status) => {
              // 教師の確認マークの更新（教師のみ使用可能）
              if (!isTeacher) return;

              const studentId = getStudentId();
              if (!studentId) return;

              const key = `${studentId}-${subjectId}`;
              const itemRef = ref(database, `tasks/${key}/${taskId}/items/${itemId}`);

              const currentItem = tasks[key]?.[taskId]?.items?.[itemId];

              if (currentItem) {
                // 同じステータスをクリックした場合は解除する
                const newStatus = currentItem.teacherCheck === status ? null : status;
                update(itemRef, { teacherCheck: newStatus })
                  .then(() => {
                    // 教師確認の更新完了
                  })
                  .catch((error) => {
                    logger.error('教師確認の更新に失敗しました', { 
                      error: error.message,
                      subjectId,
                      taskId,
                      itemId,
                      status
                    });
                    alert('教師確認の更新に失敗しました。再度お試しください。');
                  });
              }
            }}
          />
        );
      case 'quiz':
        return (
          // 新しい一問一答コンテンツ
          <QuizSection
            currentUser={currentUser}
            selectedStudent={selectedStudent}
            isTeacher={isTeacher}
          />
        );
      default:
        return null;
    }
  };

  // 残りのコンポーネントのレンダリング処理
  return (
    <div className="bg-yellow-50 min-h-screen home-wrapper">
      <DragDropContext onDragEnd={handleDragEnd}>
        <Navbar
          currentUser={currentUser}
          selectedStudent={selectedStudent}
          selectedTeacher={selectedTeacher}
          onLogout={() => {
            handleLogout();
            resetStudentSelection();
            setSelectedTeacher(null);
          }}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          onOpenMessaging={handleOpenMessaging}
          teachers={teachers}
          onOpenNotes={handleOpenNotesModal}
          onExport={exportToExcel}
        />

        <div className="max-w-screen-xl mx-auto px-4 md:flex md:gap-6">
          {/* サイドバー */}
          {(currentUser.role === 'teacher' || currentUser.isMaster) && (
            <Sidebar
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              isMaster={currentUser.isMaster}
              sidebarTab={sidebarTab}
              setSidebarTab={setSidebarTab}
              students={students}
              selectedStudent={selectedStudent}
              onSelectStudent={setSelectedStudent}
              onAddStudent={async ({ name, username, password, grade }) => {
                const hashedPassword = await hashPassword(password);
                const newStudentRef = push(ref(database, 'students'));
                const newStudent = {
                  id: newStudentRef.key,
                  name,
                  username,
                  password: hashedPassword,
                  grade,
                  order: students.length, // 初期 order を設定
                };
                set(newStudentRef, newStudent);
              }}
              onUpdateStudent={async (updatedStudent) => {
                // パスワードが平文の場合はハッシュ化する
                let finalStudent = { ...updatedStudent };
                if (updatedStudent.password && !isPasswordHashed(updatedStudent.password)) {
                  const hashedPassword = await hashPassword(updatedStudent.password);
                  finalStudent.password = hashedPassword;
                }
                set(ref(database, `students/${updatedStudent.id}`), finalStudent);
              }}
              onDeleteStudent={(id) => {
                remove(ref(database, `students/${id}`));
                if (selectedStudent?.id === id) {
                  resetStudentSelection();
                }
              }}
              teachers={teachers}
              selectedTeacher={selectedTeacher}
              onSelectTeacher={setSelectedTeacher}
              onAddTeacher={handleAddTeacher}
              onUpdateTeacher={handleUpdateTeacher}
              onDeleteTeacher={handleDeleteTeacher}
              currentUser={currentUser}
              onOpenMessaging={handleOpenMessaging}
            />
          )}

          {/* メインコンテンツ */}
          <div className="flex-1">
            {(currentUser.role === 'student' || currentUser.role === 'teacher' || selectedStudent || selectedTeacher) && (
              <>
                {/* トップレベルのタブナビゲーション */}
                <div className="mb-6">
                  {/* タブのヘッダー */}
                  <div className="flex border-b border-purple-300">
                    {pages.map((page) => (
                      <button
                        key={page.id}
                        onClick={() => setSelectedPage(page.id)}
                        className={`flex-1 px-4 py-2 -mb-px font-medium transition-colors duration-200 ${
                          selectedPage === page.id
                            ? 'border-b-2 border-purple-700 text-purple-700'
                            : 'text-purple-500 hover:text-purple-700'
                        } cursor-pointer`}
                        aria-label={`ページタブ: ${page.name}`}
                      >
                        {page.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* タブのコンテンツ */}
                <div>
                  {renderTabContent()}
                </div>

                {/* 教師のみメモモーダルを表示 */}
                {isNotesModalOpen && (
                  <Suspense fallback={<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"><div className="text-white">メモ機能を読み込み中...</div></div>}>
                    <TeacherNotesModal
                      selectedStudent={selectedStudent}
                      currentUser={currentUser}
                      onClose={handleCloseNotesModal}
                    />
                  </Suspense>
                )}
              </>
            )}
          </div>
        </div>

        {confirmDialog && (
          <ConfirmDialog
            title={confirmDialog.title}
            message={confirmDialog.message}
            onConfirm={confirmDialog.onConfirm}
            onCancel={confirmDialog.onCancel}
          />
        )}

        {/* メッセージングモーダルの表示 */}
        {isMessaging && (
          <MessagingModal
            currentUser={currentUser}
            partner={messagingPartner}
            onClose={handleCloseMessaging}
          />
        )}
      </DragDropContext>
    </div>
  );
};

// PropTypes の定義（必要に応じて調整）
SubjectTaskBoard.propTypes = {
  // 必要なプロパティがあればここに定義
};

export default SubjectTaskBoard; 